import React from 'react'
import { sanitize } from 'dompurify'
import {
  ActionElementType,
  confidenceLevels,
} from 'Components/SharedComponents/Surveys/constants'
import {
  Description,
  StepContainer,
  Title,
} from 'Components/SharedComponents/Surveys/Surveys.styled'
import { NextActions } from 'Components/SharedComponents/Surveys/NextActions/NextActions'
import { UserActions } from 'Components/SharedComponents/Surveys/UserActions/UserActions'
import { useAppState } from 'Context/AppContext/AppContext'
import { Resource } from 'Generated/academy-graphql-types'
import {
  GoalCategory,
  UserSurveyPrimaryFunction,
  UserSurveyRole,
} from 'Generated/graphql-types'
import { snakeCaseToSpaces } from 'Utils/formatters'
import {
  ButtonAction,
  CustomAction,
  Priorities,
} from 'Components/SharedComponents/Surveys/types'
import {
  sendPriorityCompletedEvent,
  sendStepTransitionEvent,
} from '../../utils'
import {
  additionalPriorityCategories,
  NeedsAssessmentWizardStep,
} from '../../constants'
import { ConfidenceLevel, HandleActionArgs } from '../../types'

type WhatAreWeMissingProps = {
  onAnyAction: ({ nextStep, callback }: HandleActionArgs) => void
  onSetPriorities: (priorities: Priorities) => void
  onSetRecommendedResources: (resources: Resource[]) => void
  priorities: Priorities
  userJourneyLength: number
  userPrimaryFunction: UserSurveyPrimaryFunction | undefined
  userRole: UserSurveyRole | undefined
}

export const WhatAreWeMissing = ({
  onAnyAction,
  onSetPriorities,
  onSetRecommendedResources,
  priorities,
  userJourneyLength,
  userPrimaryFunction,
  userRole,
}: WhatAreWeMissingProps) => {
  const { missingPriority } = priorities
  const { category } = missingPriority
  const { nonprofitOrgConfig, userConfig } = useAppState()

  const userActions: CustomAction[] = [
    {
      callback: (...args: unknown[]) => {
        onSetPriorities({
          ...priorities,
          missingPriority: {
            category: args[0] as GoalCategory,
            confidenceLevel: '',
            challenges: missingPriority.challenges || '',
            customPriorities: missingPriority.customPriorities || '',
            selectedGoals: [],
            updateNotes: '',
          },
        })
        onSetRecommendedResources([])
      },
      label: 'Choose a priority',
      options: additionalPriorityCategories,
      type: ActionElementType.SELECT,
      value: category,
    },
    {
      callback: (...args: unknown[]) =>
        onSetPriorities({
          ...priorities,
          missingPriority: {
            ...priorities.missingPriority,
            confidenceLevel: args[0] as ConfidenceLevel,
          },
        }),
      label: `From 1 to 5, how would you rate your organization’s knowledge in ${snakeCaseToSpaces(
        category,
        true
      )}?`,
      isHidden: !category || category === GoalCategory.None,
      options: confidenceLevels,
      required: true,
      type: ActionElementType.SELECT,
      value: missingPriority.confidenceLevel,
    },
    {
      callback: (...args: unknown[]) =>
        onSetPriorities({
          ...priorities,
          missingPriority: {
            ...missingPriority,
            // Ensure that the optional priority input is set to Other if the user is entering a custom priority
            category: missingPriority.category || GoalCategory.Other,
            customPriorities: sanitize(args[0] as string),
          },
        }),
      label: `If your priorities are not listed above, add them here`,
      type: ActionElementType.TEXT_FIELD,
      value: missingPriority.customPriorities || '',
    },
    {
      callback: (...args: unknown[]) =>
        onSetPriorities({
          ...priorities,
          missingPriority: {
            ...missingPriority,
            challenges: sanitize(args[0] as string),
          },
        }),
      label: `What is your organization’s main challenge regarding this area (Optional)?`,
      type: ActionElementType.TEXT_FIELD,
      value: missingPriority.challenges,
    },
  ]

  const nextStep = NeedsAssessmentWizardStep.THANK_YOU_FOR_SHARING
  const nextActions: ButtonAction[] = [
    {
      disabled:
        !missingPriority.confidenceLevel ||
        (!category && !missingPriority.customPriorities) ||
        (category === GoalCategory.Other && !missingPriority.customPriorities),
      label: 'Next',
      path: nextStep,
      saveUserState: true,
      type: ActionElementType.BUTTON,
      callback: () => {
        sendPriorityCompletedEvent({
          nonprofitOrgId: nonprofitOrgConfig.orgId,
          priority: {
            rank: 0,
            category: missingPriority.category || GoalCategory.Other,
            confidenceLevel: missingPriority.confidenceLevel,
            challenges: missingPriority.challenges || '',
            selectedGoals: missingPriority.selectedGoals,
            customPriorities: missingPriority.customPriorities,
          },
          userEmail: userConfig.email,
          userId: userConfig.id,
          userPrimaryFunction,
          userRole,
        })
        sendStepTransitionEvent({
          currentStep: NeedsAssessmentWizardStep.WHAT_ARE_WE_MISSING,
          nextStep,
          userId: userConfig.id,
          nonprofitOrgId: nonprofitOrgConfig.orgId,
          funderOrgId: nonprofitOrgConfig.funderOrgId,
          userEmail: userConfig.email,
          userPrimaryFunction,
          userJourneyLength,
          userRole,
        })
      },
    },
  ]

  return (
    <StepContainer>
      <Title>What are we missing?</Title>
      <Description>
        Choose a priority from our secondary list or capture it in the field
        below to move to the next screen.
      </Description>
      <UserActions onAnyAction={onAnyAction} userActions={userActions} />
      <NextActions nextActions={nextActions} onAnyAction={onAnyAction} />
    </StepContainer>
  )
}
